import React, { useState, useEffect, useContext } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PageSeo from '../components/PageSeo';
import { SEO } from '../utils/seoConfig';
import { 
  FaSearch, FaMapMarkerAlt, FaBriefcase, FaUser, FaBuilding,
  FaClock, FaCertificate, FaArrowRight, FaPlus, FaFilter,
  FaUsers, FaHardHat, FaTools, FaStar
} from 'react-icons/fa';

export default function Talents() {
  const { i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [ads, setAds] = useState([]);
  const [stats, setStats] = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [adType, setAdType] = useState(searchParams.get('type') || 'all');
  const [selectedSpecialty, setSelectedSpecialty] = useState(searchParams.get('specialty') || 'all');
  const [selectedContract, setSelectedContract] = useState(searchParams.get('contract') || 'all');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [locationFilter, setLocationFilter] = useState(searchParams.get('location') || '');

  useEffect(() => {
    fetchSpecialties();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchAds();
  }, [adType, selectedSpecialty, selectedContract, locationFilter]);

  const fetchSpecialties = async () => {
    try {
      const response = await axios.get(`${API}/talents/specialties`);
      setSpecialties(response.data);
    } catch (error) {
      console.error('Error fetching specialties:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/talents/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchAds = async () => {
    setLoading(true);
    try {
      let url = `${API}/talents?status=active`;
      
      if (adType !== 'all') url += `&ad_type=${adType}`;
      if (selectedSpecialty !== 'all') url += `&specialty=${selectedSpecialty}`;
      if (selectedContract !== 'all') url += `&contract_type=${selectedContract}`;
      if (locationFilter) url += `&location=${encodeURIComponent(locationFilter)}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const response = await axios.get(url);
      setAds(response.data.ads || []);
    } catch (error) {
      console.error('Error fetching ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchAds();
  };

  const getSpecialtyName = (id) => {
    const spec = specialties.find(s => s.id === id);
    return spec ? spec.name_fr : id;
  };

  const getContractLabel = (type) => {
    const labels = {
      cdi: 'CDI',
      cdd: 'CDD',
      interim: 'Intérim',
      freelance: 'Freelance',
      stage: 'Stage'
    };
    return labels[type] || type;
  };

  const getExperienceLabel = (level) => {
    const labels = {
      debutant: 'Débutant',
      junior: 'Junior (2-5 ans)',
      confirme: 'Confirmé (5-10 ans)',
      senior: 'Senior (10-15 ans)',
      expert: 'Expert (15+ ans)'
    };
    return labels[level] || level;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <PageSeo {...SEO.talents} />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A2540] to-[#1a365d] py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6">
              <FaHardHat className="text-[#00D4FF]" />
              <span className="text-white/90 text-sm font-medium">
                Marketplace RH Géotechnique
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Talents & Recrutement
            </h1>
            <p className="text-lg text-white/70 mb-8">
              Trouvez les meilleurs professionnels du secteur géotechnique et des fondations spéciales
            </p>

            {/* Search Bar */}
            <div className="bg-white rounded-2xl p-2 flex flex-col md:flex-row gap-2 shadow-xl">
              <div className="flex-1 flex items-center gap-3 px-4 py-3">
                <FaSearch className="text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Rechercher un métier, une compétence..."
                  className="border-0 focus-visible:ring-0 text-[#0A2540]"
                />
              </div>
              <div className="flex-1 flex items-center gap-3 px-4 py-3 border-t md:border-t-0 md:border-l border-gray-100">
                <FaMapMarkerAlt className="text-[#635BFF]" />
                <Input
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Ville, région..."
                  className="border-0 focus-visible:ring-0 text-[#0A2540]"
                />
              </div>
              <Button 
                onClick={handleSearch}
                className="bg-[#635BFF] hover:bg-[#5246E5] text-white px-8 py-6 rounded-xl"
              >
                Rechercher
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      {stats && (
        <section className="py-8 bg-white border-b">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-8 md:gap-16">
              <div className="text-center">
                <div className="text-3xl font-bold text-[#0A2540]">{stats.total_ads}</div>
                <div className="text-gray-500 text-sm">Annonces actives</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#635BFF]">{stats.job_offers}</div>
                <div className="text-gray-500 text-sm">Offres d'emploi</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#00D4FF]">{stats.profiles}</div>
                <div className="text-gray-500 text-sm">Profils disponibles</div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filters Sidebar */}
            <div className="lg:w-64 flex-shrink-0">
              <Card className="sticky top-24">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-[#0A2540]">Filtres</h3>
                    <FaFilter className="text-gray-400" />
                  </div>

                  {/* Ad Type */}
                  <div className="mb-6">
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Type d'annonce</label>
                    <Select value={adType} onValueChange={setAdType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toutes les annonces</SelectItem>
                        <SelectItem value="job_offer">Offres d'emploi</SelectItem>
                        <SelectItem value="profile">Profils disponibles</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Specialty */}
                  <div className="mb-6">
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Spécialité</label>
                    <Select value={selectedSpecialty} onValueChange={setSelectedSpecialty}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toutes les spécialités</SelectItem>
                        {specialties.map(spec => (
                          <SelectItem key={spec.id} value={spec.id}>{spec.name_fr}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Contract Type */}
                  <div className="mb-6">
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Type de contrat</label>
                    <Select value={selectedContract} onValueChange={setSelectedContract}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les contrats</SelectItem>
                        <SelectItem value="cdi">CDI</SelectItem>
                        <SelectItem value="cdd">CDD</SelectItem>
                        <SelectItem value="interim">Intérim</SelectItem>
                        <SelectItem value="freelance">Freelance</SelectItem>
                        <SelectItem value="stage">Stage</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* CTA */}
                  {user && (
                    <Link to="/talents/publier">
                      <Button className="w-full bg-[#635BFF] hover:bg-[#5246E5]">
                        <FaPlus className="mr-2" /> Publier une annonce
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Ads List */}
            <div className="flex-1">
              {/* Tabs */}
              <Tabs value={adType} onValueChange={setAdType} className="mb-6">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="all" className="flex items-center gap-2">
                    <FaUsers /> Toutes ({stats?.total_ads || 0})
                  </TabsTrigger>
                  <TabsTrigger value="job_offer" className="flex items-center gap-2">
                    <FaBuilding /> Offres ({stats?.job_offers || 0})
                  </TabsTrigger>
                  <TabsTrigger value="profile" className="flex items-center gap-2">
                    <FaUser /> Profils ({stats?.profiles || 0})
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {/* Results Count */}
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-600">
                  {ads.length} résultat{ads.length > 1 ? 's' : ''}
                </p>
              </div>

              {/* Loading */}
              {loading ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 border-4 border-[#635BFF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-500">Chargement des annonces...</p>
                </div>
              ) : ads.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <FaBriefcase className="text-6xl text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-[#0A2540] mb-2">
                      Aucune annonce trouvée
                    </h3>
                    <p className="text-gray-500 mb-6">
                      Modifiez vos filtres ou publiez la première annonce !
                    </p>
                    {user && (
                      <Link to="/talents/publier">
                        <Button className="bg-[#635BFF] hover:bg-[#5246E5]">
                          <FaPlus className="mr-2" /> Publier une annonce
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {ads.map((ad) => (
                    <Link key={ad.id} to={`/talents/${ad.id}`}>
                      <Card className="hover:shadow-lg transition-all cursor-pointer group">
                        <CardContent className="p-6">
                          <div className="flex flex-col md:flex-row md:items-start gap-4">
                            {/* Icon/Avatar */}
                            <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                              ad.ad_type === 'job_offer' 
                                ? 'bg-[#635BFF]/10' 
                                : 'bg-[#00D4FF]/10'
                            }`}>
                              {ad.ad_type === 'job_offer' ? (
                                <FaBuilding className="text-2xl text-[#635BFF]" />
                              ) : (
                                <FaUser className="text-2xl text-[#00D4FF]" />
                              )}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <Badge className={ad.ad_type === 'job_offer' ? 'bg-[#635BFF]' : 'bg-[#00D4FF]'}>
                                  {ad.ad_type === 'job_offer' ? 'Offre' : 'Profil'}
                                </Badge>
                                {ad.contract_types?.map(ct => (
                                  <Badge key={ct} variant="outline">{getContractLabel(ct)}</Badge>
                                ))}
                                {ad.is_featured && (
                                  <Badge className="bg-yellow-500">
                                    <FaStar className="mr-1" /> Premium
                                  </Badge>
                                )}
                              </div>

                              <h3 className="text-lg font-semibold text-[#0A2540] group-hover:text-[#635BFF] transition-colors mb-1">
                                {ad.title}
                              </h3>

                              <p className="text-gray-600 text-sm mb-3">
                                {ad.ad_type === 'job_offer' ? ad.company_name : ad.user_name}
                              </p>

                              {/* Specialties */}
                              <div className="flex flex-wrap gap-2 mb-3">
                                {ad.specialties?.slice(0, 3).map(spec => (
                                  <span key={spec} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                    {getSpecialtyName(spec)}
                                  </span>
                                ))}
                                {ad.specialties?.length > 3 && (
                                  <span className="text-xs text-gray-400">+{ad.specialties.length - 3}</span>
                                )}
                              </div>

                              {/* Meta */}
                              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                                {ad.location && (
                                  <span className="flex items-center gap-1">
                                    <FaMapMarkerAlt className="text-[#635BFF]" />
                                    {ad.location}
                                  </span>
                                )}
                                {ad.experience_level && (
                                  <span className="flex items-center gap-1">
                                    <FaCertificate className="text-[#00D4FF]" />
                                    {getExperienceLabel(ad.experience_level)}
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  <FaClock />
                                  {formatDate(ad.created_at)}
                                </span>
                              </div>
                            </div>

                            {/* Arrow */}
                            <div className="hidden md:flex items-center">
                              <FaArrowRight className="text-gray-300 group-hover:text-[#635BFF] transition-colors" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#635BFF] to-[#8B7DFF]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Vous êtes un professionnel qualifié ?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Créez votre profil gratuitement et soyez visible auprès des entreprises du secteur géotechnique
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={user ? "/talents/publier" : "/register"}>
              <Button className="bg-white text-[#635BFF] hover:bg-gray-100 px-8 py-6 text-lg">
                <FaPlus className="mr-2" /> Créer mon profil
              </Button>
            </Link>
            <Link to={user ? "/talents/publier" : "/register"}>
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                <FaBuilding className="mr-2" /> Publier une offre
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
